// Connexion du ChatGPT de la personne qui a créé le monde (mode « apporte ton ChatGPT ») : un code d'appareil à taper
// sur la page d'OpenAI, puis le monde vérifie tout seul toutes les quelques secondes. Rien n'est payé par Veillée.
import { useEffect, useState } from "preact/hooks";
import type { ClientView } from "../../shared/types";
import { CHATGPT_DEVICE_PAGE } from "../../shared/chatgpt";
import { disconnect, pushToast, send } from "../store";
import { Campfire } from "./Home";
import "./ChatGptLink.css";

const POLL_MS = 4_000;

/** Compte à rebours « 12 min » jusqu'à l'expiration du code. */
function minutesLeft(expiresAt: number | null, now: number): number | null {
  return expiresAt === null ? null : Math.max(0, Math.round((expiresAt - now) / 60_000));
}

export function ChatGptLink({ view }: { view: ClientView }) {
  const link = view.chatgpt;
  const [now, setNow] = useState(Date.now());
  const waiting = link?.state === "code";

  // tant qu'un code attend : le monde demande à OpenAI si la personne a autorisé, et à chaque retour sur l'appli
  useEffect(() => {
    if (!waiting) return;
    const poll = (): void => send({ t: "chatgpt_poll" });
    const timer = setInterval(() => { poll(); setNow(Date.now()); }, POLL_MS);
    const back = (): void => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", back);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", back); };
  }, [waiting]);

  if (!link) return null;
  const left = minutesLeft(link.expiresAt, now);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link?.userCode ?? "");
      pushToast("Code copié.", "succes");
    } catch {
      pushToast("Copie impossible : recopie-le à la main.", "danger");
    }
  }

  return (
    <main class="lien-chatgpt">
      <Campfire size={110} />
      <h1 class="lien-chatgpt-titre">Connecte ton ChatGPT</h1>
      <p class="lien-chatgpt-texte">
        Le Conteur du monde « {view.world.name} » écrira avec ton compte ChatGPT : c'est ton abonnement qui travaille, même
        gratuit. Les autres joueurs n'ont rien à faire.
      </p>

      {link.state === "code" && link.userCode ? (
        <ol class="card lien-chatgpt-etapes">
          <li>
            Ouvre la page d'OpenAI et connecte-toi à ChatGPT.
            <a class="btn btn-primary lien-chatgpt-ouvrir" href={CHATGPT_DEVICE_PAGE} target="_blank" rel="noopener noreferrer">
              Ouvrir la page d'OpenAI
            </a>
          </li>
          <li>
            Tape ce code :
            <span class="lien-chatgpt-code-ligne">
              <strong class="lien-chatgpt-code">{link.userCode}</strong>
              <button type="button" class="btn btn-ghost btn-small" onClick={() => void copy()}>Copier</button>
            </span>
          </li>
          <li>Autorise, puis reviens ici : le monde le verra tout seul et le Conteur commencera à tisser.</li>
        </ol>
      ) : (
        <div class="lien-chatgpt-action">
          {link.problem && <p class="lien-chatgpt-probleme" role="alert">{link.problem}</p>}
          <button type="button" class="btn btn-primary" onClick={() => send({ t: "chatgpt_connect" })}>Obtenir mon code</button>
        </div>
      )}

      <p class="home-hint lien-chatgpt-aide">
        {left !== null && waiting ? `Le code expire dans ${left} min. ` : ""}
        ChatGPT refuse le code ? Dans ChatGPT, Réglages → Sécurité, active la connexion par code d'appareil, puis demande un
        nouveau code.
      </p>
      {waiting && (
        <button type="button" class="btn btn-ghost btn-small" onClick={() => send({ t: "chatgpt_connect" })}>Nouveau code</button>
      )}
      <button type="button" class="btn btn-ghost btn-small" onClick={() => disconnect()}>Revenir à l'accueil</button>
    </main>
  );
}
